import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { Minus, Plus, Upload, X } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RejectionData, RejectionType, REJECTION_TYPE_LABELS } from '../../models/Receive';

interface ProductToReject {
  productId: number;
  productName: string;
  productCode: string;
  productImage: string;
  scannedQty: number;
  maxRejectQty: number;
}

interface RejectionEntry {
  quantity: number;
  rejectionType: RejectionType | '';
  rejectionText: string;
  attachments: File[];
}

export interface RejectionModalRef {
  open: (product: ProductToReject, onConfirm: (data: RejectionData) => Promise<void>) => void;
}

export const RejectionModal = forwardRef<RejectionModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const modalRef = useRef<ModalCenterRef>(null);
  const [product, setProduct] = useState<ProductToReject | null>(null);
  const [rejectionEntries, setRejectionEntries] = useState<RejectionEntry[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const confirmCallbackRef = useRef<((data: RejectionData) => Promise<void>) | null>(null);

  useImperativeHandle(ref, () => ({
    open: (productData: ProductToReject, onConfirm: (data: RejectionData) => Promise<void>) => {
      setProduct(productData);
      // Inicializar con una entrada por defecto
      setRejectionEntries([
        {
          quantity: productData.maxRejectQty,
          rejectionType: '',
          rejectionText: '',
          attachments: [],
        },
      ]);
      setIsProcessing(false);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  // Validar si se puede confirmar
  const canConfirm = () => {
    if (rejectionEntries.length === 0) return false;

    // Todas las entradas deben tener tipo y cantidad > 0
    const allValid = rejectionEntries.every((e) => e.rejectionType !== '' && e.quantity > 0);
    if (!allValid) return false;

    // La suma de cantidades debe ser <= maxRejectQty
    const totalRejected = rejectionEntries.reduce((sum, e) => sum + e.quantity, 0);
    return totalRejected <= (product?.maxRejectQty || 0) && totalRejected > 0;
  };

  const handleConfirm = async () => {
    if (!product || !confirmCallbackRef.current || !canConfirm()) return;

    setIsProcessing(true);

    try {
      const totalQty = rejectionEntries.reduce((sum, e) => sum + e.quantity, 0);
      const allAttachments: File[] = [];
      rejectionEntries.forEach((entry) => {
        allAttachments.push(...entry.attachments);
      });

      // Usar el primer tipo de rechazo como principal
      const rejectionData: RejectionData = {
        productId: product.productId,
        qty: totalQty,
        rejectionType: rejectionEntries[0].rejectionType as string,
        rejectionText: rejectionEntries[0].rejectionText || undefined,
        attachments: allAttachments,
      };

      await confirmCallbackRef.current(rejectionData);

      // Si llegamos aquí, fue exitoso - cerrar modal
      modalRef.current?.closeModal();
    } catch (error) {
      console.error('Error al confirmar rechazo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddEntry = () => {
    if (!product) return;

    const currentTotal = rejectionEntries.reduce((sum, e) => sum + e.quantity, 0);
    const remaining = product.maxRejectQty - currentTotal;

    if (remaining > 0) {
      setRejectionEntries((prev) => [
        ...prev,
        {
          quantity: remaining,
          rejectionType: '',
          rejectionText: '',
          attachments: [],
        },
      ]);
    }
  };

  const handleRemoveEntry = (index: number) => {
    setRejectionEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEntryChange = (index: number, field: keyof RejectionEntry, value: any) => {
    setRejectionEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  };

  const handleFileChange = (entryIndex: number, files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files).slice(0, 5); // Máximo 5 archivos por entrada
    setRejectionEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIndex ? { ...entry, attachments: [...entry.attachments, ...fileArray] } : entry,
      ),
    );
  };

  const handleRemoveFile = (entryIndex: number, fileIndex: number) => {
    setRejectionEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIndex
          ? {
              ...entry,
              attachments: entry.attachments.filter((_, fi) => fi !== fileIndex),
            }
          : entry,
      ),
    );
  };

  if (!product) return null;

  const currentTotal = rejectionEntries.reduce((sum, e) => sum + e.quantity, 0);
  const remaining = product.maxRejectQty - currentTotal;

  return (
    <ModalCenter
      ref={modalRef}
      title={t('rejection.modal.title', 'Rechazar Producto')}
      type="warning"
      width="default"
      className="max-h-[90vh]"
      classNameBody="overflow-auto"
    >
      <div className="space-y-6">
        {/* Información del producto */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start gap-4">
            <Product
              id={product.productId}
              name={product.productName}
              src={product.productImage}
              code={product.productCode}
              showImageModal={true}
            />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-1">
                {t('rejection.modal.scanned', 'Escaneado')}: {product.scannedQty}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('rejection.modal.max_reject', 'Máximo a rechazar')}: {product.maxRejectQty}
              </p>
            </div>
          </div>
        </div>

        {/* Entradas de rechazo */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">
              {t('rejection.modal.rejection_entries', 'Entradas de Rechazo')}
            </Label>
            {remaining > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={handleAddEntry}>
                <Plus className="w-4 h-4 mr-2" />
                {t('rejection.modal.add_entry', 'Añadir Entrada')}
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {rejectionEntries.map((entry, index) => (
              <div key={index} className="bg-background border border-border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    {t('rejection.modal.entry', 'Entrada')} {index + 1}
                  </Label>
                  {rejectionEntries.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveEntry(index)}>
                      <Minus className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Cantidad */}
                <div className="space-y-2">
                  <Label htmlFor={`qty-${index}`}>{t('rejection.modal.quantity', 'Cantidad')} *</Label>
                  <Input
                    id={`qty-${index}`}
                    type="number"
                    min="1"
                    max={index === 0 ? product.maxRejectQty : remaining + entry.quantity}
                    value={entry.quantity}
                    onChange={(e) => handleEntryChange(index, 'quantity', parseInt(e.target.value) || 0)}
                  />
                </div>

                {/* Tipo de rechazo */}
                <div className="space-y-2">
                  <Label htmlFor={`type-${index}`}>{t('rejection.modal.rejection_type', 'Tipo de Rechazo')} *</Label>
                  <Select
                    value={entry.rejectionType}
                    onValueChange={(value) => handleEntryChange(index, 'rejectionType', value)}
                  >
                    <SelectTrigger id={`type-${index}`}>
                      <SelectValue placeholder={t('rejection.modal.select_type', 'Seleccionar tipo')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(REJECTION_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Texto de rechazo */}
                <div className="space-y-2">
                  <Label htmlFor={`text-${index}`}>
                    {t('rejection.modal.rejection_text', 'Texto de Rechazo')} (opcional)
                  </Label>
                  <Input
                    id={`text-${index}`}
                    type="text"
                    value={entry.rejectionText}
                    onChange={(e) => handleEntryChange(index, 'rejectionText', e.target.value)}
                    placeholder={t('rejection.modal.text_placeholder', 'Descripción del rechazo...')}
                  />
                </div>

                {/* Adjuntar archivos */}
                <div className="space-y-2">
                  <Label>{t('rejection.modal.attachments', 'Adjuntar Evidencias')} (opcional)</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        onChange={(e) => handleFileChange(index, e.target.files)}
                        className="hidden"
                        id={`file-${index}`}
                      />
                      <Label
                        htmlFor={`file-${index}`}
                        className="flex items-center gap-2 px-4 py-2 border border-border rounded-md cursor-pointer hover:bg-accent"
                      >
                        <Upload className="w-4 h-4" />
                        {t('rejection.modal.upload_files', 'Subir archivos')}
                      </Label>
                    </div>
                    {entry.attachments.length > 0 && (
                      <div className="space-y-1">
                        {entry.attachments.map((file, fileIndex) => (
                          <div
                            key={fileIndex}
                            className="flex items-center justify-between bg-muted/30 rounded px-2 py-1 text-sm"
                          >
                            <span className="truncate flex-1">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(index, fileIndex)}
                              className="text-destructive hover:text-destructive/80"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Resumen */}
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('rejection.modal.total_rejected', 'Total a rechazar')}:</span>
              <span className="font-semibold text-foreground">{currentTotal}</span>
            </div>
            {remaining > 0 && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-muted-foreground">{t('rejection.modal.remaining', 'Restante')}:</span>
                <span className="text-orange-600 dark:text-orange-400">{remaining}</span>
              </div>
            )}
            {currentTotal > product.maxRejectQty && (
              <Alert className="mt-2">
                <AlertDescription className="text-destructive">
                  {t('rejection.modal.exceeds_max', 'La cantidad total excede el máximo permitido')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* Botones */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => modalRef.current?.closeModal()}
            className="flex-1"
            disabled={isProcessing}
          >
            {t('rejection.modal.cancel', 'Cancelar')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            className="flex-1"
            disabled={!canConfirm() || isProcessing}
          >
            {isProcessing
              ? t('rejection.modal.processing', 'Procesando...')
              : t('rejection.modal.confirm', 'Confirmar Rechazo')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

RejectionModal.displayName = 'RejectionModal';
